import { Fragment, useRef } from "react";
import uuid from "react-uuid";
import { formEl } from "../constants";
import { QuestionTypes } from "../../../types/Question";
import CustomRichTextEditor from "../../form/input/CustomRichTextEditor";
import Paper from "../../ui/customs/Paper";
import Select from "../../form/Select";
import {Copy, GripVertical, Trash2} from "lucide-react";
import MyEditor5 from "../../form/input/MyEditor5";

type Option = {
  id: string;
  uid: string;
  title: string;
  questionId?: number;
  isCorrect: number;
  fileId?: string;
  media?: string;
  mediaType?: string;
  type?: string;
  orderNo?: string;
};

type RadioInputProps = {
  item: {
    id: string;
    uid: string;
    title: string;
    value: string | null;
    valueId?: string | null;
    required: boolean;
    type: string;
    answers?: Option[];
  };
  currentType: QuestionTypes;
  handleValue: (id: string, newValue: string, type: QuestionTypes) => void;
  deleteEl: (uuid: {id?: string, uid: string}) => void;
  handleRequired: (id: string) => void;
  handleElType: (id: string, value: QuestionTypes) => void;
  addOption: (id: string, option: Option) => void;
  handleOptionValues: (itemId: string, answer: { optionId: string, uid: string, optionVal: string, isCorrect: number }, type: QuestionTypes) => void;
  deleteOption: (itemId: string, uid: string, optionId: string) => void;
  duplicateElement: (id: string, type: QuestionTypes) => void;
  isSubQuestion?: boolean;
};

const RadioInput: React.FC<RadioInputProps> = ({
                                                 item,
                                                 currentType,
                                                 handleValue,
                                                 deleteEl,
                                                 handleRequired,
                                                 handleElType,
                                                 addOption,
                                                 handleOptionValues,
                                                 deleteOption,
                                                 duplicateElement,
                                                 isSubQuestion,
                                               }) => {
  // const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<{ [key: string]: NodeJS.Timeout }>({});

  const createNewOption = (id: string) => {
    const data: Option = {
      id: "",
      uid: uuid(),
      title: "",
      isCorrect: 0,
    };
    addOption(id, data);
  };

  return (
      <Fragment>
        <Paper className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <GripVertical className="text-gray-400 cursor-move"/>
            <div className="w-1/4">
              {isSubQuestion ? (
                  <Select
                      defaultValue={currentType.toString()}
                      onChange={(val) => handleElType(item.id, Number(val))}
                      options={formEl.filter((el) => !el.hasChildren)
                          .map((el) => ({
                        label: el.label,
                        value: String(el.value),
                      }))}
                  />
              ) : (
                  <Select
                      defaultValue={currentType.toString()}
                      onChange={(val) => handleElType(item.id, Number(val))}
                      options={formEl.map((el) => ({
                        label: el.label,
                        value: String(el.value),
                      }))}
                  />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 mb-4 p-4">
            <MyEditor5
                content={item.title}
                onBlur={(value) => handleValue(item.id, value, currentType)}
            />

            {item.answers?.map((opt, key) => (
                <div key={opt.uid} className="flex items-center gap-3 border p-3 rounded bg-gray-50">
                  <input
                      type="radio"
                      name={`radio-${item.id}`}
                      checked={opt.isCorrect === 1}
                      onClick={() => {
                        handleOptionValues(
                            item.id,
                            {
                              optionId: opt.id,
                              uid: opt.uid,
                              optionVal: opt.title,
                              isCorrect: 1,
                            },
                            currentType
                        )
                      }
                      }
                      className="form-radio text-indigo-600"
                  />
                  <MyEditor5
                      content={opt.title}
                      placeholder={`Option ${key + 1}`}
                      className={'flex-1 rounded text-sm'}
                      onBlur={(newValue) => {
                        // Dừng timeout cũ nếu có (cho từng option)
                        if (timeoutRef.current[opt.uid]) {
                          clearTimeout(timeoutRef.current[opt.uid]);
                        }

                        // Gán timeout mới cho từng option
                        timeoutRef.current[opt.uid] = setTimeout(() => {
                          handleOptionValues(
                              item.id,
                              {
                                optionId: opt.id,
                                uid: opt.uid,
                                optionVal: newValue,
                                isCorrect: opt.isCorrect,
                              },
                              currentType
                          );
                        }, 800);
                      }}
                  />
                  <button
                      onClick={() => deleteOption(item.id, opt.uid, opt.id)}
                      className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
            ))}

            <button
                onClick={() => createNewOption(item.id)}
                className="text-sm text-blue-600 hover:underline"
            >
              + Thêm lựa chọn
            </button>
          </div>

          <div className="mt-4 flex items-center space-x-4 border-t pt-3">
            <button
                className="text-gray-500 hover:text-gray-800"
                onClick={() => deleteEl({ id: item.id, uid: item.uid })}
                title="Xoá phần tử"
            >
              <Trash2 size={18} />
            </button>
            <button
                className="text-gray-500 hover:text-gray-800"
                onClick={() => duplicateElement(item.id, currentType)}
                title="Nhân bản"
            >
              <Copy size={18} />
            </button>

            <label className="inline-flex items-center ml-auto text-sm text-gray-600">
              <input
                  type="checkbox"
                  checked={item.required}
                  onChange={() => handleRequired(item.id)}
                  className="mr-2"
              />
              Bắt buộc
            </label>
          </div>
        </Paper>
      </Fragment>
  );
};

export default RadioInput;
